import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

/**
 * Statsig feature gate that AUTHORIZES the `?is_ds=true` preview override — in any environment,
 * including production. It is NOT the DS decision itself: the two-level rollout
 * (`ds_rollout_page_config__web` + `RAMP_ds-rollout__web`) owns that (see `resolveDSRolloutGateValue`).
 * Fail-closed: with the gate off (or Statsig unreachable) the query param is ignored everywhere.
 */
export const DESIGN_SYSTEM_GATE_NAME = 'ssr-use-design-system-components'
export const DESIGN_SYSTEM_QUERY_PARAM_KEY = 'is_ds'

export const DOCUMENT_HTML_DATA_STYLES_ATTR = 'data-styles'
export const DOCUMENT_HTML_DATA_STYLES_VALUE = 'panda'

// Marks `<html>` for DS ramp `test`-bucket users (independent of the page's own DS decision).
export const DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR = 'data-ds-ramp-user'
export const DOCUMENT_HTML_DATA_DS_RAMP_USER_VALUE = 'true'

/**
 * Per-request DS decision for renders. Written by `_app` from
 * `pageProps.isDesignSystemEnabled` (computed in `getServerSideBaseProps` by the two-level
 * DS rollout — `ds_rollout_page_config__web` + `RAMP_ds-rollout__web` — or the gate-authorized
 * `?is_ds=true` override). Safe as module state because the pages-router render pass is
 * synchronous. Browser: `undefined` means "no decision seeded yet" (first paint before `_app`
 * runs, or component tests that stamp the attribute directly) — readers fall back to the
 * `_document`-stamped `<html>` attribute. `_app` itself never leaves it `undefined`: the
 * decision is per-page, so pages without base props pin fail-closed `false` (see `_app`) and
 * the commit-time sync clears the scope to match their legacy markup.
 */
let designSystemEnabledForRender: boolean | undefined

export function setDesignSystemEnabledInMemory(enabled: boolean | undefined | null): void {
  if (isBrowser()) {
    // `undefined` = reset to the attribute fallback (test ergonomics + the pre-`_app` window;
    // `_app` pins no-decision pages to `false` instead — the scope is per-page). The attribute
    // itself is only mutated at commit time by syncDesignSystemHtmlAttributes(): a render-phase
    // mutation restyles the still-visible outgoing page while the incoming tree renders (the
    // is_ds toggle styling flash).
    designSystemEnabledForRender = enabled === undefined ? undefined : Boolean(enabled)
    return
  }

  // Server: always reset — fail-closed, never leak the previous request's value.
  designSystemEnabledForRender = Boolean(enabled)
}

export function shouldUseDesignSystem(): boolean {
  if (isBrowser()) {
    if (designSystemEnabledForRender !== undefined) {
      return designSystemEnabledForRender
    }
    return document.documentElement.getAttribute(DOCUMENT_HTML_DATA_STYLES_ATTR) === DOCUMENT_HTML_DATA_STYLES_VALUE
  }

  return designSystemEnabledForRender ?? false
}

/** Ramp `test` membership for this request (independent of page DS). SSR module flag + `<html>` attr. */
let dsRampUserForRender: boolean | undefined

export function setDSRampUserInMemory(enabled: boolean | undefined | null): void {
  if (isBrowser()) {
    // Same commit-time contract as the DS decision above — no DOM mutation during render.
    dsRampUserForRender = enabled === undefined ? undefined : Boolean(enabled)
    return
  }

  // Server: always reset — fail-closed, never leak the previous request's value.
  dsRampUserForRender = Boolean(enabled)
}

export function isDSRampUser(): boolean {
  if (isBrowser()) {
    if (dsRampUserForRender !== undefined) {
      return dsRampUserForRender
    }
    return (
      document.documentElement.getAttribute(DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR) ===
      DOCUMENT_HTML_DATA_DS_RAMP_USER_VALUE
    )
  }

  return dsRampUserForRender ?? false
}

/**
 * Sole client-side writer of the `data-styles` / `data-ds-ramp-user` `<html>` attributes —
 * the CSS scope for every Panda/DS rule (`_document` stamps them on full loads). Called by
 * `_app` from a layout effect: after the navigated-to tree commits, before the browser
 * paints, so markup and style scope flip in the same frame. Undecided scopes (`undefined` —
 * the pre-`_app` window, attribute-stamping tests, and the per-user ramp flag on pages that
 * omit it) are left untouched.
 */
export function syncDesignSystemHtmlAttributes(): void {
  if (!isBrowser()) {
    return
  }

  if (designSystemEnabledForRender !== undefined) {
    if (designSystemEnabledForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_STYLES_ATTR, DOCUMENT_HTML_DATA_STYLES_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_STYLES_ATTR)
    }
  }

  if (dsRampUserForRender !== undefined) {
    if (dsRampUserForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR, DOCUMENT_HTML_DATA_DS_RAMP_USER_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_DS_RAMP_USER_ATTR)
    }
  }
}

/** DS chrome when the page is DS, or the user is in the DS ramp `test` bucket. */
export function shouldUseDSHeaderFooter(): boolean {
  return shouldUseDesignSystem() || isDSRampUser()
}

export function isDesignSystemEnabledFromNextData(nextData: {
  props?: {
    pageProps?: {
      isDesignSystemEnabled?: boolean
      userAgnosticGlobalFeatureGates?: Record<string, boolean | undefined>
    }
  }
  query?: Record<string, string | string[] | undefined>
}): boolean {
  // The SSR-computed decision is authoritative in EVERY environment, including
  // production. This is the same value `shouldUseDesignSystem()` reads
  // server-side, and this function is the sole writer of `data-styles="panda"`
  // on `<html>` (`_document.page.tsx`). If the check were placed behind the
  // gate-authorized preview fallback below, production SSR would render DS while
  // `_document` left the attribute unset → client reads legacy → hydration
  // mismatch, and DS could never ship to prod traffic via the rollout.
  if (nextData.props?.pageProps?.isDesignSystemEnabled) {
    return true
  }

  // The `is_ds` query-param override is a QA affordance authorized exclusively by the
  // `ssr-use-design-system-components` Statsig gate — honored in ANY environment (including
  // production) while the gate is on, ignored everywhere while it is off. The gate ships in
  // page props (`FEATURE_GATE_REGISTRY`), so this stays in lockstep with the SSR-side check
  // in `getServerSideBaseProps`.
  if (nextData.props?.pageProps?.userAgnosticGlobalFeatureGates?.[DESIGN_SYSTEM_GATE_NAME]) {
    const queryValue = nextData.query?.[DESIGN_SYSTEM_QUERY_PARAM_KEY]
    return (Array.isArray(queryValue) ? queryValue[0] : queryValue) === 'true'
  }

  return false
}

/** SSR ramp prop for `_document`'s initial `data-ds-ramp-user` stamp (QA override is folded server-side). */
export function isDSRampUserFromNextData(nextData: {
  props?: {
    pageProps?: {
      isDSRampUser?: boolean
    }
  }
}): boolean {
  return Boolean(nextData.props?.pageProps?.isDSRampUser)
}
